import { Tabs, TabsList, TabsTrigger } from "@acme/ui/tabs";
import { createFileRoute, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddPlayerDialogV2 } from "~/common/components/add-player-dialog-v2.js";
import { NotificationButton } from "~/common/components/enable-notifications-button.js";
import { InfoBoardDialog } from "~/common/components/info-board-dialog.js";
import { InstallPWAButton } from "~/common/components/install-pwa-button.js";
import { IOSInstallPrompt } from "~/common/components/ios-install-prompt.js";
import { SettingsDropdown } from "~/common/components/settings-dropdown.js";
import { TestingDropdown } from "~/common/components/testing-dropdown.js";
import { usePlatformFcmEvents } from "~/common/hooks/use-platform-fcm-events.js";
import { useVerificationNotifications } from "~/common/hooks/use-verification-notifications.js";
import { AuthContext } from "~/features/auth/_providers/auth.provider.js";

const MAIN_NAV_TABS = [
  { value: "dashboard", labelKey: "navigation.dashboard" },
  { value: "players", labelKey: "navigation.myPlayers" },
  { value: "clans", labelKey: "navigation.myClans" },
] as const;

export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: "/sign-in",
        search: {
          redirect: location.href, // Full URL path
        },
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const location = useLocation();
  const { auth: routerAuth } = Route.useRouteContext();
  const authFromReactContext = useContext(AuthContext);
  // Prefer router context (TanStack pattern); fallback to React AuthContext because
  // useRouteContext() can be undefined on first render in layout routes (TanStack Router #1618, #1637).
  const auth = routerAuth ?? authFromReactContext ?? undefined;
  const userId = auth?.isAuthenticated ? auth.user?.id : undefined;
  const isAdmin = Boolean(auth?.isAuthenticated && auth.user.role === "admin");
  useVerificationNotifications(userId);
  usePlatformFcmEvents(isAdmin);

  const getActiveTab = () => {
    if (location.pathname === "/my-players" || location.pathname.startsWith("/my-players/")) {
      return "players";
    }
    if (location.pathname === "/my-clans" || location.pathname.startsWith("/my-clans/")) {
      return "clans";
    }
    return "dashboard";
  };

  const navigate = Route.useNavigate();

  const handleTabChange = (value: string) => {
    if (value === "dashboard") {
      void navigate({ to: "/" });
    } else if (value === "players") {
      void navigate({ to: "/my-players" });
    } else if (value === "clans") {
      void navigate({ to: "/my-clans" });
    }
  };

  const headerActions = (
    <div className="flex items-center gap-2 justify-end">
      <InstallPWAButton />
      <NotificationButton />
      <TestingDropdown />
      <InfoBoardDialog />
      <SettingsDropdown />
    </div>
  );

  return (
    <Tabs
      className="min-h-screen bg-background flex flex-col"
      onValueChange={handleTabChange}
      value={getActiveTab()}>
      <IOSInstallPrompt />
      <header className="sticky top-0 z-10 border-b border-border bg-card dark:bg-[oklch(0.155_0_0)]">
        <div className="container mx-auto flex items-center justify-end gap-4 px-4 py-3 sm:justify-between sm:py-4">
          <TabsList className="hidden w-auto overflow-x-auto sm:inline-flex">
            {MAIN_NAV_TABS.map(({ value, labelKey }) => (
              <TabsTrigger key={value} value={value}>
                {t(labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
          {headerActions}
        </div>
      </header>

      <main className="flex-1 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] sm:pb-0">
        <AddPlayerDialogV2 onOpenChange={setShowAddPlayerDialog} open={showAddPlayerDialog} />
        <Outlet />
      </main>

      <nav
        aria-label={t("navigation.mainAriaLabel")}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] dark:bg-[oklch(0.155_0_0)] sm:hidden">
        <TabsList className="grid h-14 w-full grid-cols-3 rounded-none bg-transparent p-1">
          {MAIN_NAV_TABS.map(({ value, labelKey }) => (
            <TabsTrigger className="h-full rounded-md px-1 text-[11px] leading-tight" key={value} value={value}>
              {t(labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
      </nav>
    </Tabs>
  );
}
